import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { forkJoin, Observable, of, ReplaySubject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { saveAs } from 'file-saver';

import { SectorsService } from '../../../../sectors/sectors.service';
import { BrigadesService } from '../brigades/brigades.service';
import { AppSettingsService } from '../../../../app-settings/app-settings.service';
import { AuthService } from '../../../../auth/auth.service';
import { SectorRole } from '../../../../sectors/sectors';

@Injectable({
  providedIn: 'root',
})
export class TicketsAssignService {
  private automaticDistributionApi = {
    start: '/admin/tickets/auto_assign/',
    status: '/admin/tickets/auto_assign_result/',
    check: '/admin/tickets/auto_assign_exists/',
    result: '/admin/tickets/auto_assign_result_data/',
    assignResult: '/admin/tickets/auto_assign_result_assign/',
    employees: '/admin/tickets/auto_assign_employees/',
    recalculate: '/admin/tickets/auto_assign_recalculate/',
    unassignedOrderAnalysis: '/admin/tickets/auto_assign_unassigned_order_analysis/',
  };

  private gpDopInfoCache: { [ticketNumber: string]: ReplaySubject<any> } = {};
  private gpDopInfoPending: number[] = [];
  private gpDopInfoFlushScheduled = false;

  constructor(
    private plotsService: SectorsService,
    private brigadesService: BrigadesService,
    private appSettingsService: AppSettingsService,
    private http: HttpClient,
    private authService: AuthService
  ) {}

  getDistricts(params): Observable<any> {
    return this.plotsService.getDistricts(params);
  }

  getTicketsTypes(): Observable<any> {
    return this.http.get(`/admin/ticket_types/`);
  }

  getTicketsStatuses(): Observable<any> {
    return this.http.get(`/admin/ticket_statuses/`);
  }

  getBrigadesTruncated(params): Observable<any> {
    return this.brigadesService.getBrigadesTruncated(params);
  }

  getBrigades(params): Observable<any> {
    return this.http.get(`/admin/brigades/available/`, { params: params });
  }

  assignBrigades(data): Observable<any> {
    return this.http.post(`/async/ticket/assign/`, data);
  }

  checkAutomaticDistribution(params): Observable<any> {
    return this.http.get(this.automaticDistributionApi.check, { params: params });
  }

  startAutomaticDistribution(data): Observable<any> {
    return this.http.post(this.automaticDistributionApi.start, data);
  }

  startAutomaticDistributionRecalculate(data): Observable<any> {
    return this.http.post(this.automaticDistributionApi.recalculate, data);
  }

  getAutomaticDistributionStatus(transactionId): Observable<any> {
    return this.http.get(this.automaticDistributionApi.status, {
      params: { transaction_id: String(transactionId) },
    });
  }

  getAutomaticDistributionResult(params): Observable<any> {
    return this.http.get(this.automaticDistributionApi.result, { params: params });
  }

  getAutomaticDistributionUnassignedOrderAnalysis(transactionId): Observable<any> {
    return this.http.get(this.automaticDistributionApi.unassignedOrderAnalysis, {
      params: { transaction_id: String(transactionId) },
    });
  }

  assignAutomaticDistributionResult(data): Observable<any> {
    return this.http.post(this.automaticDistributionApi.assignResult, data);
  }

  getAutomaticDistributionEmployees(sectorId: number, date: string, limitationType: 1 | 2 | 3): Observable<any> {
    return this.http.get(this.automaticDistributionApi.employees, {
      params: {
        sector_id: String(sectorId),
        data: date,
        limitation_type: String(limitationType),
      },
    });
  }

  getTicketsStatus(params): Observable<any> {
    return this.http.get(`/admin/tickets/todays_count/`, {
      params: params,
    });
  }

  getTickets(params): Observable<any> {
    this.resetGPDopInfoCache();

    return this.http.get(`/admin/tickets/`, { params: params });
  }

  downloadTickets(params): Observable<HttpResponse<Blob>> {
    return this.http
      .get(`/admin/tickets/download-csv/`, {
        params: params,
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        tap((response) => {
          const contentDisposition = response.headers.get('content-disposition');
          let filename = 'tickets';

          if (contentDisposition) {
            const matches = contentDisposition.match(/filename="(.+)?"/);

            if (matches && matches[1]) {
              filename = matches[1];
            }
          }

          if (response.body) {
            saveAs(response.body, filename);
          }
        })
      );
  }

  getGPTypes(): Observable<any> {
    return this.http.get('/helpdesk/ticket_types');
  }

  getGPSubTypes(): Observable<any> {
    return this.http.get('/helpdesk/global_problem_subtypes');
  }

  getResponsibleGroups(): Observable<any> {
    return this.http.get('/helpdesk/responsible_groups');
  }

  getResponsibleUsers(): Observable<any> {
    return this.http.get('/helpdesk/responsible_users');
  }

  getGpCheck(house_id): Observable<any> {
    return this.http.get(`/async/ticket/check_gp/`, {
      params: { house_id: house_id },
    });
  }

  getTicketsSettings(): Observable<any> {
    return this.appSettingsService.getSettings();
  }

  getTicketsHDStatuses({ role }): Observable<any>  {
    return this.http.get(`/admin/ticket_statuses_groups/`, { params : { role } });
  }

  getTicketsGPDopInfo(data): Observable<any> {
    return this.http.post(`/tickets/detail_tickets_info`, data);
  }

  /**
   * Доп. информация по ГП для карточек списка.
   * Карточки регистрируют номера синхронно в одном цикле отрисовки, поэтому запрос
   * `detail_tickets_info` уходит один на все номера, а результат раздаётся по номеру заявки.
   * Кэш живёт до следующей загрузки списка (см. getTickets).
   */
  getTicketGPDopInfo(ticketNumber: number | string): Observable<any> {
    const key = String(ticketNumber);

    if (!this.gpDopInfoCache[key]) {
      this.gpDopInfoCache[key] = new ReplaySubject<any>(1);
      this.gpDopInfoPending.push(+ticketNumber);
      this.scheduleGPDopInfoFlush();
    }

    return this.gpDopInfoCache[key].asObservable();
  }

  private resetGPDopInfoCache(): void {
    this.gpDopInfoCache = {};
    this.gpDopInfoPending = [];
  }

  private scheduleGPDopInfoFlush(): void {
    if (this.gpDopInfoFlushScheduled) {
      return;
    }

    this.gpDopInfoFlushScheduled = true;
    Promise.resolve().then(() => {
      this.gpDopInfoFlushScheduled = false;
      this.flushGPDopInfo();
    });
  }

  private flushGPDopInfo(): void {
    const ticketNumbers = this.gpDopInfoPending;
    const cache = this.gpDopInfoCache;
    this.gpDopInfoPending = [];

    if (!ticketNumbers.length) {
      return;
    }

    const resolve = (tickets: any[]) => {
      ticketNumbers.forEach((ticketNumber) => {
        const subject = cache[String(ticketNumber)];
        if (subject) {
          subject.next(this.findGPDopInfo(tickets, ticketNumber));
          subject.complete();
        }
      });
    };

    this.getTicketsGPDopInfo({ ticket_numbers: ticketNumbers }).subscribe(
      (response) => resolve((response && response.data && response.data.tickets) || []),
      () => resolve([])
    );
  }

  private findGPDopInfo(tickets: any[], ticketNumber: number): any {
    const key = String(ticketNumber);

    return tickets.find((ticket) => String(ticket.ticket_number) === key || String(ticket.hd_ticket_number) === key) || null;
  }

  getTicketsMultipleStatuses(params): Observable<any> {
    const ticketsHDStatuses = this.getTicketsHDStatuses(params);
    const ticketsVKStatuses = this.getTicketsStatuses();

    return forkJoin([ticketsHDStatuses, ticketsVKStatuses]);
  }

  getTicketsUnion(sector_id, tickets_params): Observable<any> {
    const params = {
      sector_id: sector_id,
      role: this.authService.isGRSUser() ? SectorRole.GRRS : SectorRole.MT
    };
    const districts = this.getDistricts({ sectors_ids: [sector_id] });
    const ticketsTypes = this.getTicketsTypes();
    const brigades = this.getBrigadesTruncated(params);
    const ticketsStatuses = this.getTicketsStatus(params);
    const tickets = this.getTickets(tickets_params);
    const ticketsSettings = this.getTicketsSettings();
    const ticketsHDStatuses = this.getTicketsHDStatuses(params);
    const ticketsVKStatuses = this.getTicketsStatuses();

    return forkJoin([districts, ticketsTypes, brigades, ticketsStatuses, tickets, ticketsSettings, ticketsHDStatuses, ticketsVKStatuses]);
  }

  getPeriodFilterData(params): Observable<any> {
    const districts = this.getDistricts({
      sectors_ids: [params.sector_id],
    });

    const ticketsVKStatuses = this.getTicketsStatuses();
    const ticketsHDStatuses = this.getTicketsHDStatuses(params);
    const ticketsTypes = this.getTicketsTypes();
    const gpTypes = this.authService.isGRSUser() ? this.getGPTypes() : of({ data: [] });
    const gpSubTypes = this.authService.isGRSUser() ? this.getGPSubTypes() : of({ data: [] });
    const responsibleGroups = this.authService.isGRSUser() ? this.getResponsibleGroups() : of({ data: [] });
    const responsibleUsers = this.authService.isGRSUser() ? this.getResponsibleUsers() : of({ data: [] });
    const brigades = this.getBrigadesTruncated(params);
    const settings = this.getTicketsSettings();

    return forkJoin([
      districts,
      ticketsVKStatuses,
      ticketsHDStatuses,
      ticketsTypes,
      brigades,
      settings,
      gpTypes,
      gpSubTypes,
      responsibleGroups,
      responsibleUsers
    ]);
  }

  createDeparture(brigadeId, ticketIds) {
    return this.http.post('/admin/brigade_rms/', {
      brigade_id: brigadeId,
      tickets_id: ticketIds
    });
  }
}
